<template>
  <div>
  <v-data-table
    :headers="headers"
    :items="items"
    :custom-sort="customSort"
    class="striped-table ordera-box-shadow mb-6"
    :loading="loading"
    mobile-breakpoint="0"
    hide-default-footer
    :items-per-page="-1"
  >
    <template v-slot:item.num="{ item }">
      {{ item.orders_guest && item.orders_guest[0] ? item.orders_guest[0].id : "" }}
    </template>

    <template v-slot:item.nameAndType="{ item }">
      <div
        class="user-name-link primary--text font-weight-medium"
        @click="$emit('show-user-details', item)"
      >
        {{ toLatin(item.name) }} {{ toLatin(item.lastname) }}
      </div>
      <div class="text-caption grey--text">
        {{ item.phone }}
      </div>
    </template>

    <template v-slot:item.restaurant="{ item }">
      <div
        v-if="item.orders_guest && item.orders_guest[0] && item.orders_guest[0].restaurant"
        class="restaurant-link primary--text"
        @click="$emit('show-restaurant-details', item.orders_guest[0].restaurant)"
      >
        <v-icon small color="orange" class="mr-1">mdi-store</v-icon>
        {{ item.orders_guest[0].restaurant.name }}
      </div>
    </template>

    <template v-slot:item.address="{ item }">
      <div v-if="item.orders_guest && item.orders_guest[0]">
        <div v-if="item.orders_guest[0].delivery_type === 0">
          <v-icon small class="mr-1">mdi-motorbike</v-icon>
          {{ $t("delivery_to_address") }}
          <br />
          <span v-if="item.orders_guest[0].location">
            {{ item.orders_guest[0].location.address }}
          </span>
        </div>
        <div v-else-if="item.orders_guest[0].delivery_type === 1">
          <v-icon small class="mr-1">mdi-silverware-fork-knife</v-icon>
          <span>{{ $t("pickup_in_restaurant") }}</span>
        </div>
        <div v-else-if="item.orders_guest[0].delivery_type === 2">
          <v-icon small class="mr-1">mdi-home</v-icon>
          {{ $t("consumption_in_restaurant") }}
        </div>
      </div>
    </template>

    <template v-slot:item.time="{ item }">
      <div v-if="item.orders_guest && item.orders_guest[0]">
        <div class="font-weight-medium">
          {{ formatOrderTime(item.orders_guest[0]) }}
        </div>
        <div v-if="isAsapOrder(item.orders_guest[0])" class="asap-label primary--text">
          {{ $t("asap") }}
        </div>
      </div>
    </template>

    <template v-slot:item.orders="{ item }">
      <div v-if="item.orders_guest && item.orders_guest.length">
        <div
          v-for="(order, idx) in item.orders_guest"
          :key="idx"
          :class="{ 'order-block-divider': idx > 0 }"
        >
          <div
            v-for="(transaction, tidx) in order.order_guest_transactions"
            :key="tidx"
            class="d-flex align-center order-item-row"
          >
            <span class="qty-label mr-1">{{ transaction.quantity }}×</span>
            <span class="text-truncate">
              {{ transaction?.food?.name || transaction?.meals?.name || "" }}
            </span>
          </div>
          <a
            href="#"
            class="text-decoration-none order-details-link"
            @click.prevent="$emit('show-details', { order, guest: item })"
          >
            {{ $t("order_details") }}
          </a>
        </div>
      </div>
    </template>

    <template v-slot:item.amount="{ item }">
      <div v-if="item.orders_guest && item.orders_guest.length" class="py-2">
        <div
          v-for="(order, idx) in item.orders_guest"
          :key="idx"
          class="amount-row font-weight-medium"
          :class="{ 'order-block-divider': idx > 0 }"
        >
          {{ orderSubtotal(order).toFixed(1) }} {{ orderCurrency(order) }}
        </div>
      </div>
    </template>

    <template v-slot:item.total="{ item }">
      <span class="font-weight-bold primary--text">
        {{ calculateTotal(item).toFixed(1) }} {{ itemCurrency(item) }}
      </span>
    </template>

    <template v-slot:item.status="{ item }">
      <v-menu v-if="item.orders_guest && item.orders_guest[0]" offset-y left>
        <template v-slot:activator="{ on, attrs }">
          <v-chip
            small
            :style="statusChipStyle(item.orders_guest[0].state)"
            v-bind="attrs"
            v-on="on"
            class="cursor-pointer status-chip"
            :loading="changingStatusOrderId === item.orders_guest[0].id"
          >
            <span
              class="status-dot mr-1"
              :style="statusDotStyle(item.orders_guest[0].state)"
            ></span>
            {{ $t(item.orders_guest[0].state) }}
          </v-chip>
        </template>
        <v-list dense>
          <v-list-item
            v-for="status in orderStatuses"
            :key="status.value"
            @click="requestStatusChange(item.orders_guest[0], item, status.value)"
            :disabled="item.orders_guest[0].state === status.value"
          >
            <v-list-item-icon class="mr-2">
              <v-icon small :style="{ color: getStatusColors(status.value).fg }">
                {{ status.icon }}
              </v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t(status.value) }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>
  </v-data-table>
  <prep-time-dialog
    v-model="prepTimeDialogOpen"
    @select-minutes="confirmPrepMinutes"
    @ready-now="confirmReadyNow"
  />
  </div>
</template>

<script>
import hasGuestOrderStatus from "@/mixins/report/hasGuestOrderStatus";
import PrepTimeDialog from "@/components/guest/PrepTimeDialog.vue";

export default {
  name: "ReportGuestOrderTableDesktop",
  components: { PrepTimeDialog },
  mixins: [hasGuestOrderStatus],
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    currency: {
      type: String,
      default: "",
    },
    changingStatusOrderId: {
      type: [Number, String],
      default: null,
    },
  },
  computed: {
    isAdmin() {
      return this.$store.state.user.user?.type === "admin";
    },
    headers() {
      const baseHeaders = [
        { text: this.$t("r_b"), value: "num", sortable: true, width: "90px" },
        {
          text: this.$t("name_surname_phone"),
          value: "nameAndType",
          sortable: true,
        },
      ];

      if (this.isAdmin) {
        baseHeaders.splice(1, 0, {
          text: this.$t("restaurant"),
          value: "restaurant",
          sortable: true,
        });
      }

      baseHeaders.push(
        { text: this.$t("address"), value: "address", sortable: false },
        { text: this.$t("time"), value: "time", sortable: true, width: "110px" },
        { text: this.$t("ordered"), value: "orders", sortable: false },
        {
          text: this.$t("amount"),
          value: "amount",
          sortable: true,
          width: "110px",
          align: "end",
        },
        {
          text: this.$t("total"),
          value: "total",
          sortable: true,
          width: "110px",
          align: "end",
        },
        { text: this.$t("status"), value: "status", sortable: true, width: "150px" }
      );

      return baseHeaders;
    },
  },
  methods: {
    // Currency is per order's restaurant, not the logged-in restaurant.
    orderCurrency(order) {
      return order?.restaurant?.settings?.price || this.currency || this.restaurantCurrency;
    },
    itemCurrency(item) {
      return this.orderCurrency(item?.orders_guest?.[0]);
    },
    getSortValue(item, key) {
      const order = item.orders_guest && item.orders_guest[0];
      switch (key) {
        case "num":
          return order ? order.id : 0;
        case "restaurant":
          return order && order.restaurant ? order.restaurant.name.toLowerCase() : "";
        case "nameAndType":
          return `${item.name || ""} ${item.lastname || ""}`.trim().toLowerCase();
        case "time":
          return order ? order.delivery_time || order.created_at || "" : "";
        case "amount":
          return order ? this.orderSubtotal(order) : 0;
        case "total":
          return this.calculateTotal(item);
        case "status": {
          const idx = this.orderStatuses.findIndex(
            (s) => s.value === (order && order.state)
          );
          return idx === -1 ? this.orderStatuses.length : idx;
        }
        default:
          return item[key];
      }
    },
    customSort(items, sortBy, sortDesc) {
      if (!sortBy.length) return items;

      return [...items].sort((a, b) => {
        for (let i = 0; i < sortBy.length; i++) {
          const key = sortBy[i];
          const desc = sortDesc[i];
          const valA = this.getSortValue(a, key);
          const valB = this.getSortValue(b, key);

          if (valA < valB) return desc ? 1 : -1;
          if (valA > valB) return desc ? -1 : 1;
        }
        return 0;
      });
    },
  },
};
</script>

<style scoped>
.restaurant-link {
  cursor: pointer;
}

.restaurant-link:hover {
  text-decoration-style: solid;
}

.user-name-link {
  cursor: pointer;
  color: inherit;
}

.user-name-link:hover {
  color: #fb6d3b;
  text-decoration: underline;
}

.status-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.status-chip {
  font-weight: 600;
  transition: transform 0.15s ease;
}

.status-chip:hover {
  transform: translateY(-1px);
}

.asap-label {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin-top: 2px;
}

.qty-label {
  display: inline-block;
  text-align: right;
  color: rgba(0, 0, 0, 0.64);
  font-weight: 700;
  margin-right: 2px;
}

.order-item-row {
  font-size: 13px;
  margin-bottom: 3px;
}

.order-details-link {
  font-size: 11.5px;
  font-weight: 700;
  color: #fb6d3b;
}

.amount-row {
  white-space: nowrap;
}

.order-block-divider {
  padding-top: 6px;
  margin-top: 4px;
  border-top: 1px dashed #e1dfda;
}

.striped-table >>> thead th {
  background: #efeeeb !important;
  color: #9a968e !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid #e1dfda !important;
}

.striped-table >>> tbody td {
  border-bottom: 1px solid #e1dfda !important;
}

.striped-table >>> tbody tr {
  transition: background-color 0.15s ease;
}

.striped-table >>> tbody tr:hover {
  background-color: rgba(251, 109, 59, 0.06) !important;
}

.striped-table >>> td {
  vertical-align: middle;
  padding-top: 10px !important;
  padding-bottom: 10px !important;
}

.striped-table.v-data-table >>> tr:nth-of-type(odd) {
  background-color: #f9f8f7 !important;
}
</style>
